import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  message,
  Popconfirm,
  Tooltip,
  Switch,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  LinkOutlined,
  UserOutlined,
  GlobalOutlined,
} from '@ant-design/icons';

const { Option } = Select;

const DomainManagement: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [form] = Form.useForm();

  // 模拟域名数据
  const domainData = [
    {
      id: 1,
      domain: 'promo001.example.com',
      agentId: 'agent_001',
      agentName: '代理A',
      status: 'enabled',
      remark: '主推广域名',
      createdAt: '2025-01-15 10:00:00',
      updatedAt: '2025-10-23 14:30:00',
    },
    {
      id: 2,
      domain: 'promo002.example.com',
      agentId: 'agent_002',
      agentName: '代理B',
      status: 'enabled',
      remark: '备用推广域名',
      createdAt: '2025-02-20 11:00:00',
      updatedAt: '2025-10-22 16:20:00',
    },
    {
      id: 3,
      domain: 'promo003.example.com',
      agentId: 'agent_001',
      agentName: '代理A',
      status: 'disabled',
      remark: '测试域名',
      createdAt: '2025-03-10 09:00:00',
      updatedAt: '2025-10-21 10:15:00',
    },
    {
      id: 4,
      domain: 'promo004.example.com',
      agentId: 'agent_003',
      agentName: '代理C',
      status: 'enabled',
      remark: '新推广域名',
      createdAt: '2025-09-01 14:00:00',
      updatedAt: '2025-10-20 09:30:00',
    },
    {
      id: 5,
      domain: 'promo005.example.com',
      agentId: 'agent_002',
      agentName: '代理B',
      status: 'enabled',
      remark: '',
      createdAt: '2025-08-15 10:30:00',
      updatedAt: '2025-10-19 15:45:00',
    },
  ];

  // 模拟代理列表
  const agentList = [
    { id: 'agent_001', name: '代理A' },
    { id: 'agent_002', name: '代理B' },
    { id: 'agent_003', name: '代理C' },
    { id: 'agent_004', name: '代理D' },
    { id: 'agent_005', name: '代理E' },
  ];

  // 过滤数据
  const filteredData = domainData.filter(item => {
    const matchesSearch = !searchText || 
      item.domain.toLowerCase().includes(searchText.toLowerCase()) ||
      item.agentName.toLowerCase().includes(searchText.toLowerCase());
    const matchesStatus = filterStatus === 'all' || item.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // 统计数据
  const statsData = {
    total: filteredData.length,
    enabled: filteredData.filter(item => item.status === 'enabled').length,
    disabled: filteredData.filter(item => item.status === 'disabled').length,
  };

  const handleAdd = () => {
    setSelectedRecord(null);
    form.resetFields();
    setEditModalVisible(true);
  };

  const handleEdit = (record: any) => {
    setSelectedRecord(record);
    form.setFieldsValue(record);
    setEditModalVisible(true);
  };

  const handleDelete = (record: any) => {
    message.loading('正在删除域名...', 1);
    setTimeout(() => {
      message.success(`已删除域名：${record.domain}`);
    }, 1000);
  };

  const handleBatchDelete = () => {
    message.loading('正在批量删除...', 1);
    setTimeout(() => {
      message.success(`已删除 ${selectedRowKeys.length} 个域名`);
      setSelectedRowKeys([]);
    }, 1000);
  };

  const handleSave = async (values: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      message.success(selectedRecord ? '域名修改成功！' : '域名添加成功！');
      setEditModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('操作失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    message.loading('正在刷新数据...', 1);
    setTimeout(() => {
      setLoading(false);
      message.success('数据刷新成功！');
    }, 1500);
  };

  const columns = [
    {
      title: '域名',
      dataIndex: 'domain',
      key: 'domain',
      width: 250,
      render: (text: string) => (
        <Space>
          <GlobalOutlined />
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: '绑定代理',
      dataIndex: 'agentName',
      key: 'agentName',
      width: 150,
      render: (text: string, record: any) => (
        <Space>
          <UserOutlined />
          <span>{text}</span>
          <Tag color="blue">{record.agentId}</Tag>
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={status === 'enabled' ? 'green' : 'red'}>
          {status === 'enabled' ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      ellipsis: true,
      render: (text: string) => text || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 160,
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_: any, record: any) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个域名吗？"
            onConfirm={() => handleDelete(record)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  return (
    <div style={{ padding: 24 }}>
      {/* 统计卡片 */}
      <Space size="large" style={{ marginBottom: 24 }}>
        <Card>
          <Space>
            <GlobalOutlined style={{ fontSize: 24, color: '#1890ff' }} />
            <div>
              <div style={{ fontSize: 20, fontWeight: 'bold' }}>{statsData.total}</div>
              <div style={{ color: '#999', fontSize: 14 }}>总域名数</div>
            </div>
          </Space>
        </Card>
        <Card>
          <Space>
            <GlobalOutlined style={{ fontSize: 24, color: '#52c41a' }} />
            <div>
              <div style={{ fontSize: 20, fontWeight: 'bold' }}>{statsData.enabled}</div>
              <div style={{ color: '#999', fontSize: 14 }}>已启用</div>
            </div>
          </Space>
        </Card>
        <Card>
          <Space>
            <GlobalOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />
            <div>
              <div style={{ fontSize: 20, fontWeight: 'bold' }}>{statsData.disabled}</div>
              <div style={{ color: '#999', fontSize: 14 }}>已禁用</div>
            </div>
          </Space>
        </Card>
      </Space>

      <Card
        title="域名管理"
        extra={
          <Space>
            <Input
              placeholder="搜索域名或代理名称"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
              allowClear
            />
            <Select
              value={filterStatus}
              onChange={setFilterStatus}
              style={{ width: 120 }}
            >
              <Option value="all">全部状态</Option>
              <Option value="enabled">已启用</Option>
              <Option value="disabled">已禁用</Option>
            </Select>
            <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>
              刷新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              新增域名
            </Button>
            {selectedRowKeys.length > 0 && (
              <Popconfirm
                title={`确定要删除选中的 ${selectedRowKeys.length} 个域名吗？`}
                onConfirm={handleBatchDelete}
                okText="确定"
                cancelText="取消"
              >
                <Button danger icon={<DeleteOutlined />}>
                  批量删除
                </Button>
              </Popconfirm>
            )}
          </Space>
        }
      >
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          scroll={{ x: 1200 }}
          pagination={{
            total: filteredData.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
        />
      </Card>

      {/* 编辑/新增模态框 */}
      <Modal
        title={selectedRecord ? '编辑域名' : '新增域名'}
        open={editModalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setEditModalVisible(false);
          form.resetFields();
        }}
        confirmLoading={loading}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          initialValues={{
            status: 'enabled',
          }}
        >
          <Form.Item
            name="domain"
            label="域名地址"
            rules={[
              { required: true, message: '请输入域名' },
              {
                pattern: /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9])+$/,
                message: '请输入有效的域名格式',
              },
            ]}
          >
            <Input
              prefix={<GlobalOutlined />}
              placeholder="例如：promo.example.com"
              disabled={!!selectedRecord}
            />
          </Form.Item>

          <Form.Item
            name="agentId"
            label="绑定代理"
            rules={[{ required: true, message: '请选择绑定的代理' }]}
          >
            <Select placeholder="请选择代理">
              {agentList.map(agent => (
                <Option key={agent.id} value={agent.id}>
                  {agent.name} ({agent.id})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="status"
            label="状态"
            valuePropName="checked"
          >
            <Switch
              checkedChildren="启用"
              unCheckedChildren="禁用"
            />
          </Form.Item>

          <Form.Item
            name="remark"
            label="备注"
          >
            <Input.TextArea
              rows={4}
              placeholder="请输入备注信息"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DomainManagement;

